import {
  Organization,
  getOrganizationsRequest,
  createOrUpdateOrganizationRequest,
  disableOrEnableOrganizationRequest,
} from '@models/organization.model';
import { graphQlClient } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import { normalizeEmail } from '@utils/normalizeEmail';

export interface GetOrganizationsParams {
  page?: number;
  limit?: number;
  search?: string;
  filter?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface CreateOrganizationPayload {
  name: string;
  ownerEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  showAsSubcontractor?: boolean;
  atavEnabled?: boolean;
}

export interface UpdateOrganizationPayload {
  organizationId: string;
  name: string;
  status?: string;
  showAsSubcontractor?: boolean;
  atavEnabled?: boolean;
  // Sprint 12, story-052 (U72).
  completenessGateMode?: 'WARN' | 'BLOCK';
}

export interface CreateOrganizationWithPaymentPayload {
  organizationName: string;
  paymentMethodType?: string;
  billingCycle: string;
  planId?: string; // Selected subscription plan ID
  // Use existing payment method from another organization
  existingPaymentMethodId?: string;
  // Required only if NOT using existing payment method
  firstName?: string;
  lastName?: string;
  email?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  company?: string;
  paymentToken?: string;
  ccNumber?: string;
  ccExp?: string;
  cvv?: string;
  googlePaymentData?: string;
}

export const organizationService = {
  async getUserOrganizations(): Promise<Organization[]> {
    const data: { getUserOrganizations: Organization[] } = await graphQlClient.request(
      OrganizationsGraphQL.queries.getUserOrganizations,
    );

    if (!data?.getUserOrganizations) {
      throw new Error('Failed to fetch user organizations');
    }

    return data.getUserOrganizations;
  },

  async getOrganization(organizationId: string): Promise<Organization> {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    const data: { getOrganizationByID: Organization } = await graphQlClient.request(
      OrganizationsGraphQL.queries.getOrganizationByID,
      { id: organizationId },
    );

    if (!data?.getOrganizationByID) {
      throw new Error('Failed to fetch organization');
    }

    return data.getOrganizationByID;
  },

  async getOrganizations(params: GetOrganizationsParams = {}): Promise<{
    organizations: Organization[];
    count: number;
  }> {
    const variables = {
      organizations: {
        page: params.page || 0,
        limit: params.limit || 100,
        search: params.search || undefined,
        filter: params.filter || 'Active',
        orderBy: params.orderBy || 'name',
        orderDirection: params.orderDirection || 'asc',
      },
    };

    const data: getOrganizationsRequest = await graphQlClient.request(
      OrganizationsGraphQL.queries.getOrganizations,
      variables,
    );

    if (!data?.getOrganizations) {
      throw new Error('Failed to fetch organizations');
    }

    return {
      organizations: data.getOrganizations.organizations || [],
      count: data.getOrganizations.count || 0,
    };
  },

  async createOrganization(
    payload: CreateOrganizationPayload,
  ): Promise<Organization> {
    if (
      !payload.name ||
      !payload.ownerEmail ||
      !payload.ownerFirstName ||
      !payload.ownerLastName
    ) {
      throw new Error(
        'Name, owner email, first name and last name are required',
      );
    }

    const variables = {
      organization: {
        name: payload.name,
        ownerEmail: normalizeEmail(payload.ownerEmail),
        ownerFirstName: payload.ownerFirstName,
        ownerLastName: payload.ownerLastName,
        showAsSubcontractor: payload.showAsSubcontractor ?? true,
        atavEnabled: payload.atavEnabled ?? false,
      },
    };

    const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
      OrganizationsGraphQL.mutations.createOrganization,
      variables,
    );

    if (!data?.createOrganization) {
      throw new Error('Failed to create organization');
    }

    return data.createOrganization;
  },

  async updateOrganization(
    payload: UpdateOrganizationPayload,
  ): Promise<Organization> {
    if (!payload.organizationId || !payload.name) {
      throw new Error('Organization ID and name are required');
    }

    const variables = {
      organization: {
        organizationId: payload.organizationId,
        name: payload.name,
        showAsSubcontractor: payload.showAsSubcontractor,
        atavEnabled: payload.atavEnabled,
      },
    };

    const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
      OrganizationsGraphQL.mutations.updateOrganization,
      variables,
    );

    if (!data?.updateOrganization) {
      throw new Error('Failed to update organization');
    }

    return data.updateOrganization;
  },

  async disableOrganization(organizationId: string): Promise<Organization> {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    const data: disableOrEnableOrganizationRequest =
      await graphQlClient.request(
        OrganizationsGraphQL.mutations.disableOrganization,
        { id: organizationId },
      );

    if (!data?.disableOrganization) {
      throw new Error('Failed to disable organization');
    }

    return data.disableOrganization;
  },

  async enableOrganization(organizationId: string): Promise<Organization> {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    const data: disableOrEnableOrganizationRequest =
      await graphQlClient.request(
        OrganizationsGraphQL.mutations.enableOrganization,
        { id: organizationId },
      );

    if (!data?.enableOrganization) {
      throw new Error('Failed to enable organization');
    }

    return data.enableOrganization;
  },

  async setInvoicingPaused(
    organizationId: string,
    paused: boolean,
  ): Promise<Organization> {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    const data: { setOrganizationInvoicingPaused: Organization } =
      await graphQlClient.request(
        OrganizationsGraphQL.mutations.setOrganizationInvoicingPaused,
        { id: organizationId, paused },
      );

    if (!data?.setOrganizationInvoicingPaused) {
      throw new Error('Failed to update invoicing status');
    }

    return data.setOrganizationInvoicingPaused;
  },

  async setInvoicingPausedBulk(
    organizationIds: string[],
    paused: boolean,
  ): Promise<number> {
    if (!organizationIds?.length) {
      return 0;
    }

    const data: { setOrganizationsInvoicingPaused: number } =
      await graphQlClient.request(
        OrganizationsGraphQL.mutations.setOrganizationsInvoicingPaused,
        { ids: organizationIds, paused },
      );

    return data?.setOrganizationsInvoicingPaused ?? 0;
  },

  async setEnabledBulk(
    organizationIds: string[],
    enable: boolean,
  ): Promise<number> {
    if (!organizationIds?.length) {
      return 0;
    }

    const data: { setOrganizationsEnabled: number } =
      await graphQlClient.request(
        OrganizationsGraphQL.mutations.setOrganizationsEnabled,
        { ids: organizationIds, enable },
      );

    return data?.setOrganizationsEnabled ?? 0;
  },

  async getMyInvoicingPaused(): Promise<boolean> {
    const data: { getMyOrganizationInvoicingPaused: boolean } =
      await graphQlClient.request(
        OrganizationsGraphQL.queries.getMyOrganizationInvoicingPaused,
      );

    return !!data?.getMyOrganizationInvoicingPaused;
  },

  /**
   * Create an additional organization on a card-free trial.
   * Throws FREE_TRIAL_ORGANIZATION_LIMIT_REACHED when the user is already at the cap.
   */
  async createOrganizationWithTrial(payload: {
    organizationName: string;
    showAsSubcontractor?: boolean;
  }): Promise<{ organization: Organization; subscription: any }> {
    if (!payload.organizationName) {
      throw new Error('Organization name is required');
    }

    const data: {
      createOrganizationWithTrial: {
        organization: Organization;
        subscription: any;
      };
    } = await graphQlClient.request(
      OrganizationsGraphQL.mutations.createOrganizationWithTrial,
      { input: payload },
    );

    return data.createOrganizationWithTrial;
  },

  async createOrganizationWithPayment(
    payload: CreateOrganizationWithPaymentPayload,
  ): Promise<{
    organization: Organization;
    subscription: any;
  }> {
    if (!payload.organizationName) {
      throw new Error('Organization name is required');
    }

    const variables = {
      input: payload,
    };

    const data: {
      createOrganizationWithPayment: {
        organization: Organization;
        subscription: any;
      };
    } = await graphQlClient.request(
      OrganizationsGraphQL.mutations.createOrganizationWithPayment,
      variables,
    );

    if (!data?.createOrganizationWithPayment) {
      throw new Error('Failed to create organization with payment');
    }

    return data.createOrganizationWithPayment;
  },
};

export default organizationService;
